import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { istDateKey } from '@/lib/ist'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  Row,
  Strip,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { V1Photo } from '@/features/gate/v1/photo'
import { Refused } from '@/features/gate/v1/scan'
import { C } from '@/features/gate/v1/style'
import { cropWord, kg, rosterShort, shortLot, tr } from '@/features/gate/v1/text'
import type { PackRunView, ProcessState } from '@/features/process/projection'
import { OkPill, TickDot } from '@/features/process/v1/ui'
import { useDraftState } from '@/features/process/useProcess'
import { KIcon } from './icons'
import { startPackRunV1 } from './hallApi'
import { allocatedRuns, batchFigures, bestBeforeFrom, dateBoxes, isStarted, runHold } from './model'
import { Lead, Locked, Note, PackHeader, WithHall, YesSheet } from './ui'
import { callFailed, errorCode, ki, lineHeader, packSize, productName } from './words'
import { useHall } from './useHall'
import { DelabelRows } from './Delabel'
import { HallTile, HeldRunsTile, LotsTile } from './Lots'

interface StartDraft {
  /** The empty-hopper photo (media id), kept until the start is saved. */
  photo: string | null
  /** Cleanout done was tapped. */
  cleaned: boolean
  /** The server said the last run on the line was another crop: full clean. */
  allergen: boolean
}

const emptyStart = (): StartDraft => ({ photo: null, cleaned: false, allergen: false })

/** V1-F7-02a — आज: पैकिंग. The allocated run, its cleanout and the start. */
export function V1PackHome() {
  return <WithHall>{(state) => <Home state={state} />}</WithHall>
}

function Home({ state }: { state: ProcessState }) {
  const runs = allocatedRuns(state)
  const started = runs.find(isStarted)
  // One run at a time on the line: a started run opens straight away.
  if (started) return <Navigate to={`/m/pack/run/${started.id}`} replace />
  const run = runs[0]
  if (!run) return <NoRun state={state} />
  return <StartRun key={run.id} state={state} run={run} others={runs.length - 1} />
}

/** No run allocated yet: the office allocates it in the console (F7.1). */
function NoRun({ state }: { state: ProcessState }) {
  const actor = useActor()
  const hall = useHall(state)
  return (
    <V1Screen
      nav
      header={<PackHeader icon="home" title={tr('pack.v1.home.title')} sub={actor.name} />}
      footer={
        <Footer>
          {hall.online ? (
            <Cta
              label={tr('pack.v1.home.refresh')}
              icon="download"
              busy={hall.busy}
              onClick={hall.refresh}
            />
          ) : (
            <WaitCta>{tr('pack.v1.needsNet')}</WaitCta>
          )}
        </Footer>
      }
    >
      <ItemRow
        padding="10px 12px"
        lead={<Lead icon="pouch" />}
        title={tr('pack.v1.home.none')}
        line={<span style={{ color: C.muted }}>{tr('pack.v1.home.noneLine')}</span>}
      />
      <HeldRunsTile state={state} />
      <DelabelRows state={state} />
      <LotsTile />
      <HallTile />
    </V1Screen>
  )
}

function StartRun({
  state,
  run,
  others,
}: {
  state: ProcessState
  run: PackRunView
  others: number
}) {
  const navigate = useNavigate()
  const actor = useActor()
  const [draft, update, loaded, clear] = useDraftState<StartDraft>(
    `pack.v1.start:${run.id}`,
    'pack.v1.start',
    emptyStart,
    actor.user_id,
  )
  const hall = useHall(state)
  const [view, setView] = useState<'home' | 'clean'>('home')
  const [sheet, setSheet] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (!loaded) return <V1Screen />

  const fullClean = run.allergen_clean_required || draft.allergen
  const fig = batchFigures(state, run)
  const hold = runHold(state, run)
  const labelId = run.label_version_id ?? run.label_versions[0]?.id ?? null
  const header = lineHeader(run)

  if (view === 'clean')
    return (
      <Cleanout
        run={run}
        fullClean={fullClean}
        photo={draft.photo}
        setPhoto={(photo) => update((d) => ({ ...d, photo, cleaned: false }))}
        onBack={() => setView('home')}
        onDone={() => {
          update((d) => ({ ...d, cleaned: true }))
          setView('home')
        }}
      />
    )

  const start = async () => {
    if (!draft.photo || !labelId) return
    setSaving(true)
    setErr(null)
    try {
      await startPackRunV1(run, {
        cleanout_photo_id: draft.photo,
        allergen_clean: fullClean,
        label_version_id: labelId,
      })
      await clear()
      navigate(`/m/pack/run/${run.id}`, { replace: true })
    } catch (e) {
      if (errorCode(e) === 'ALLERGEN_CLEAN_MISSING') {
        // The server knows the last crop on the line: full clean, then a new photo.
        update((d) => ({ ...d, allergen: true, cleaned: false }))
        setSheet(false)
      }
      setErr(callFailed(e, tr('pack.v1.start.failed')))
    } finally {
      setSaving(false)
    }
  }

  const cleaned = draft.cleaned && !!draft.photo
  const today = istDateKey()
  const bb = bestBeforeFrom(today, run.sku.shelf_life_days)
  const [y, mm, dd] = today.split('-')

  return (
    <V1Screen
      nav
      header={
        <PackHeader lot={header} icon="home" title={tr('pack.v1.home.title')} sub={actor.name} />
      }
      footer={
        <Footer>
          {err && <Refused text={err} />}
          {hold.held ? (
            <WaitCta>{tr('pack.v1.home.held')}</WaitCta>
          ) : !cleaned ? (
            <Cta
              label={tr('pack.v1.clean.cta')}
              icon={ki('broom')}
              onClick={() => setView('clean')}
            />
          ) : !labelId ? (
            <WaitCta>{tr('pack.v1.home.noLabel')}</WaitCta>
          ) : hall.online ? (
            <Cta
              label={tr('pack.v1.start.cta')}
              icon={ki('play')}
              onClick={() => (setErr(null), setSheet(true))}
            />
          ) : (
            <WaitCta>{tr('pack.v1.needsNet')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        sheet ? (
          <YesSheet
            title={tr('pack.v1.start.title')}
            yes={tr('pack.v1.start.yes')}
            yesIcon="play"
            busy={saving}
            onYes={() => void start()}
            onClose={() => setSheet(false)}
            top={150}
          >
            <ItemRow
              padding="8px 10px"
              lead={<Lead icon="groundnut" />}
              title={`${productName(run)} · ${packSize(run.sku.net_g)}`}
              line={tr('pack.v1.start.line', {
                line: header.name,
                sacks: fig.sacks,
                pouches: run.planned_pouches,
              })}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
              <TickDot size={22} />
              {tr('pack.v1.start.cleaned')}
            </span>
            <section
              style={{
                background: C.surface,
                border: `1.5px solid ${C.rule}`,
                borderRadius: 12,
                padding: '8px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 800 }}>
                {tr('pack.v1.start.lot', { code: `PL-${y}-${mm}${dd}-…` })}
              </span>
              <span style={{ fontSize: 17, fontWeight: 700 }}>
                {tr('pack.v1.start.dates', {
                  pkd: dateBoxes(today).join(' '),
                  bb: bb ? dateBoxes(bb).join(' ') : '—',
                })}
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <KIcon name="qr" size={20} />
                {tr('pack.v1.start.qr')}
              </span>
            </section>
            <Locked>{tr('pack.v1.start.locked')}</Locked>
            {err && <Refused text={err} />}
          </YesSheet>
        ) : undefined
      }
    >
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '8px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lead icon="groundnut" />
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={{ fontSize: 19, fontWeight: 700 }}>
              {tr('pack.v1.home.size', {
                size: packSize(run.sku.net_g),
                pouches: run.planned_pouches,
              })}
            </span>
            <span style={{ fontSize: 15 }}>
              {tr('pack.v1.home.batch', { sacks: fig.sacks, kg: kg(fig.g) })}
            </span>
          </span>
        </div>
        <div style={{ borderTop: `1px solid ${C.rule}` }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 22,
              background: C.photo,
              color: C.photoInk,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Icon name="user" size={36} sw={1.6} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={{ fontSize: 17, fontWeight: 700 }}>
              {run.farmer_code
                ? tr('pack.v1.farmerNo', { no: rosterShort(run.farmer_code) })
                : tr('pack.v1.home.batchOnly')}
            </span>
            <span style={{ fontSize: 15, color: C.muted }}>
              {tr('pack.v1.batchTail', { tail: shortLot(run.batch_code) })}
            </span>
          </span>
        </div>
      </section>
      {hold.held && (
        <Note icon="hourglass" tone="warn">
          {tr('pack.v1.home.heldNote')}
        </Note>
      )}
      {fullClean && (
        <Note icon="broom" tone="warn">
          {run.previous_crop_name
            ? tr('pack.v1.clean.lastCrop', { crop: cropWord(run.previous_crop_name) })
            : tr('pack.v1.clean.full')}
        </Note>
      )}
      <JobRow
        n={1}
        icon="broom"
        title={tr('pack.v1.home.job1')}
        line={tr('pack.v1.home.job1Line')}
        done={cleaned}
        current={!cleaned}
        onClick={() => setView('clean')}
      />
      <JobRow
        n={2}
        icon="play"
        title={tr('pack.v1.home.job2')}
        line={tr('pack.v1.home.job2Line')}
        done={false}
        current={cleaned}
        onClick={() =>
          cleaned && hall.online && labelId && !hold.held
            ? setSheet(true)
            : setView(cleaned ? 'home' : 'clean')
        }
      />
      {others > 0 && <Strip icon="clock">{tr('pack.v1.home.others', { count: others })}</Strip>}
      <HeldRunsTile state={state} />
      <DelabelRows state={state} />
      <LotsTile />
      <HallTile />
    </V1Screen>
  )
}

/** One step of the start (लाइन साफ़, पैकिंग शुरू) as on the board. */
function JobRow({
  n,
  icon,
  title,
  line,
  done,
  current,
  onClick,
}: {
  n: number
  icon: 'broom' | 'play'
  title: string
  line: string
  done: boolean
  current: boolean
  onClick: () => void
}) {
  return (
    <ItemRow
      padding="10px 12px"
      onClick={onClick}
      border={current ? C.acc : C.border}
      borderWidth={current ? 2 : 1.5}
      lead={<Lead icon={icon} bg={current ? C.acc : C.sunk} color={current ? '#ffffff' : C.acc} />}
      title={<span style={{ fontSize: 20 }}>{title}</span>}
      line={<span style={{ color: C.muted }}>{line}</span>}
      right={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {done ? (
            <TickDot size={30} />
          ) : (
            <span
              aria-hidden="true"
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                border: `2px solid ${current ? C.acc : C.border}`,
                color: current ? C.acc : C.border,
                fontSize: 18,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {n}
            </span>
          )}
          <span style={{ color: current ? C.acc : C.muted, display: 'flex' }}>
            <Icon name="next" size={24} sw={2.5} />
          </span>
        </span>
      }
    />
  )
}

/** V1-F7-02b — लाइन साफ़ करें: one photo of the empty hopper; the full clean folded in. */
function Cleanout({
  run,
  fullClean,
  photo,
  setPhoto,
  onBack,
  onDone,
}: {
  run: PackRunView
  fullClean: boolean
  photo: string | null
  setPhoto: (id: string | null) => void
  onBack: () => void
  onDone: () => void
}) {
  return (
    <V1Screen
      header={
        <PackHeader
          onBack={onBack}
          lot={lineHeader(run)}
          icon="broom"
          title={tr('pack.v1.clean.title')}
          step={1}
          of={2}
        />
      }
      footer={
        <Footer>
          {photo ? (
            <Cta
              label={tr(fullClean ? 'pack.v1.clean.doneFull' : 'pack.v1.clean.done')}
              icon={ki('broom')}
              onClick={onDone}
            />
          ) : (
            <WaitCta>{tr('pack.v1.clean.wait')}</WaitCta>
          )}
        </Footer>
      }
    >
      {fullClean && (
        <Note icon="broom" tone="warn">
          {run.previous_crop_name
            ? tr('pack.v1.clean.lastCrop', { crop: cropWord(run.previous_crop_name) })
            : tr('pack.v1.clean.full')}
        </Note>
      )}
      <V1Photo
        value={photo}
        onChange={setPhoto}
        render={({ preview, busy, take }) => (
          <ItemRow
            padding="8px 10px"
            border={photo ? C.ok : C.rule}
            borderWidth={photo ? 2 : 1.5}
            lead={
              preview ? (
                <img
                  src={preview}
                  alt=""
                  style={{ width: 54, height: 44, borderRadius: 8, objectFit: 'cover' }}
                />
              ) : (
                <span
                  aria-hidden="true"
                  style={{
                    width: 54,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 8,
                    background: C.photo,
                    color: C.photoInk,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <KIcon name="hopper" size={22} />
                  <Icon name="camera" size={18} />
                </span>
              )
            }
            title={tr('pack.v1.clean.photo')}
            line={photo ? <OkPill>{tr('pack.v1.clean.taken')}</OkPill> : undefined}
            right={
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn icon="camera" disabled={busy} onClick={take}>
                  {tr(photo ? 'pack.v1.clean.again' : 'pack.v1.clean.take')}
                </Btn>
              </div>
            }
          />
        )}
      />
      <Row>
        <span style={{ fontSize: 16, color: C.muted, lineHeight: 1.35 }}>
          {tr('pack.v1.clean.hint')}
        </span>
      </Row>
    </V1Screen>
  )
}
